import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sun, Battery, ShieldCheck, Wrench, Zap, Award, ChevronDown, Check, FileText, Download } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import Seo from "@/components/Seo";

const PANELS = [
 { power: "280 Wc", uses: ["Petits systèmes", "Remplacement", "Sites isolés"] },
 { power: "400 Wc", uses: ["Maisons", "Commerces", "Bureaux"] },
 { power: "405 Wc", featured: true, uses: ["Maisons familiales", "Villas standard", "PME"], note: "Le meilleur équilibre entre dimensions, rendement, facilité d'installation et coût. C'est aujourd'hui la référence NANDA SÁ ENERGIA PRO." },
 { power: "410 Wc", uses: ["Villas", "Hôtels", "Restaurants"] },
 { power: "415 Wc", uses: ["PME", "Bâtiments professionnels", "Grandes installations résidentielles"] },
];

const STORAGE = [
 { capacity: "3 kWh", uses: ["Appartement", "Petite maison"] },
 { capacity: "6 kWh", uses: ["Maison familiale", "Commerce"] },
 { capacity: "9 kWh", uses: ["Villa", "Restaurant"] },
 { capacity: "12 kWh", uses: ["Entreprise", "Hôtel", "École", "Centre de santé"] },
];

const KITS = [
 { name: "Kit 3 kWh", target: "Appartement · Petite maison · Boutique", items: ["Éclairage", "Télévision", "Internet", "Ventilateurs", "Réfrigérateur basse consommation"] },
 { name: "Kit 6 kWh", target: "Maison familiale · Restaurant · Commerce", items: ["Pompe à eau", "Congélateur", "Ordinateurs", "Éclairage complet"] },
 { name: "Kit 9 kWh", target: "Villa · Grand commerce · Hôtel", items: ["Climatisation", "Équipements professionnels", "Salle informatique"] },
 { name: "Kit 12 kWh", target: "Entreprise · École · Centre de santé", items: ["Restaurant", "Atelier", "Bureaux tertiaires"] },
 { name: "Kit 18 kWh", target: "Industrie légère · Exploitation agricole", items: ["Pompage solaire", "Chambre froide", "Grandes installations"] },
];

const METHOD = [
 "Étude", "Analyse des besoins", "Dimensionnement", "Devis",
 "Installation", "Mise en service", "Formation", "Maintenance",
];

const FAQ = [
 { q: "Quelle puissance choisir ?", a: "Cela dépend de vos usages, de la surface disponible et du profil de consommation. Une étude personnalisée est systématiquement réalisée avant tout devis." },
 { q: "Quelle autonomie prévoir ?", a: "Nos systèmes hybrides avec stockage Q.SAVE-G3 offrent une autonomie sur mesure : quelques heures pour l'essentiel, ou jour et nuit selon la capacité batterie choisie." },
 { q: "Les batteries sont-elles évolutives ?", a: "Oui. La gamme Qcells Q.SAVE-G3 est évolutive : vous pouvez ajouter des modules au fil du temps sans changer l'onduleur." },
 { q: "Combien de temps dure une installation ?", a: "De quelques jours pour un kit résidentiel à quelques semaines pour un projet tertiaire ou industriel. Le planning est communiqué à la signature du devis." },
 { q: "Quelle garantie ?", a: "Les panneaux, onduleurs et batteries bénéficient de la garantie constructeur d'origine. NANDA SÁ ENERGIA PRO assure la mise en service, la formation et le suivi." },
 { q: "Intervenez-vous au Sénégal et en Guinée-Bissau ?", a: "Nos équipes opèrent depuis Canchungo (Guinée-Bissau) et Ziguinchor (Sénégal). Pour tout projet en Gambie ou dans la sous-région, contactez-nous pour une évaluation." },
];

const Card = ({ children, className = "",...p }) => (
 <div className={`bg-white border border-nanda-line ${className}`} {...p}>{children}</div>
);

const Section = ({ children, className = "" }) => (
 <section className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">{children}</div>
 </section>
);

const Overline = ({ children }) => (
 <div className="label-overline text-nanda-muted mb-4">{children}</div>
);

export default function EnergiaProPremium() {
 const { locale } = useLanguage();
 const [openFaq, setOpenFaq] = React.useState(null);

 return (
 <div data-testid="page-energia-pro-premium">
 <Seo
 title="NANDA SÁ ENERGIA PRO. Froid, climatisation, énergie solaire, équipements techniques B2B."
 description="Partenaire technique multi-expertise en Afrique de l'Ouest : réfrigération industrielle, chambres froides, climatisation professionnelle, énergie solaire photovoltaïque, stockage, outillage, consommables et EPI. Étude, installation, maintenance."
 path="/marques/energia-pro"
 />

 {/* JSON-LD FAQPage · rich snippets Google (réutilise FAQ existante) */}
 <script
 type="application/ld+json"
 dangerouslySetInnerHTML={{
 __html: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: FAQ.map((f) => ({
 "@type": "Question",
 name: f.q,
 acceptedAnswer: { "@type": "Answer", text: f.a },
 })),
 }),
 }}
 />

 {/* Breadcrumbs visuels (le schema BreadcrumbList est déjà généré par <Seo/>) */}
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-24 md:pt-28 pb-2">
 <Breadcrumbs
 items={[
 { label: locale === "en" ? "Brands" : locale === "pt" ? "Marcas" : "Marques", to: "/marques" },
 { label: "ENERGIA PRO" },
 ]}
 />
 </div>

 {/* HERO */}
 <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden bg-nanda-ink">
 <img src="/energia-pro-installation.jpg" alt="Installation photovoltaïque NANDA SÁ ENERGIA PRO, Guinée-Bissau" className="absolute inset-0 h-full w-full object-cover opacity-70" loading="eager" />
 <div className="absolute inset-0 bg-gradient-to-b from-nanda-ink/40 via-nanda-ink/20 to-nanda-ink/70" />
 <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 md:pb-32">
 <FadeUp>
 <div className="label-overline text-white/85 mb-6">NANDA SÁ ENERGIA PRO</div>
 <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-4xl">
 Solutions techniques pour le froid, l'air et l'énergie.
 </h1>
 <p className="mt-8 max-w-2xl text-white/85 text-lg md:text-xl leading-relaxed">
 Un partenaire unique pour vos projets B2B : énergie solaire (diagnostic, dimensionnement, équipements, installation), quincaillerie professionnelle, soudure & équipements, réfrigération, climatisation, consommables et EPI en Afrique de l'Ouest.
 </p>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link to="/devis" data-testid="energia-hero-cta-devis" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-8 py-4 label-overline hover:bg-nanda-gold hover:text-white transition-colors duration-500">Demander un devis <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
 <a href="#catalogue" data-testid="energia-hero-cta-catalogue" className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500">Télécharger le catalogue <ChevronDown size={16} strokeWidth={1.5} /></a>
 </div>
 </FadeUp>
 </div>
 </section>

 {/* À PROPOS */}
 <Section>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14 items-start">
 <FadeUp className="md:col-span-5">
 <Overline>À propos</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Une expertise multi-technique, un même partenaire.</h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] leading-relaxed space-y-5">
 <p>NANDA SÁ ENERGIA PRO est la marque technique B2B de NANDA SÁ, dédiée aux entreprises, collectivités, institutions et professionnels qui recherchent un opérateur unique pour la conception, la fourniture, l'installation et la maintenance de leurs équipements techniques en Afrique de l'Ouest.</p>
 <p>Notre cœur de métier historique est la réfrigération industrielle et commerciale et la climatisation professionnelle. Nous avons progressivement étendu notre offre à l'énergie solaire photovoltaïque, au stockage lithium, à l'outillage frigoriste, aux consommables et aux équipements de protection individuelle.</p>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
 {["Étude", "Fourniture", "Installation", "Maintenance"].map((s) => (
 <div key={s} className="border-l border-nanda-gold pl-4 py-1"><div className="label-overline text-nanda-muted">{s}</div></div>
 ))}
 </div>
 </FadeUp>
 </div>
 </Section>

 {/* NANDA SÁ PRO. TROIS PÔLES DE L'OFFRE (architecture catalogue) */}
 <Section className="bg-nanda-bgAlt" id="poles">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>NANDA SÁ PRO</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Trois pôles, une même rigueur technique.</h2>
 <p className="mt-6 max-w-2xl text-nanda-inkSoft text-[17px] leading-relaxed">
 L'offre NANDA SÁ PRO s'organise autour de trois pôles complémentaires. Les catalogues quincaillerie et soudure sont en préparation : les références, photos et prix seront publiés à réception des produits.
 </p>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {[
 { title: "Énergie solaire", desc: "Équipements, diagnostic, dimensionnement et installation. Notre activité majeure, du kit résidentiel au projet industriel.", link: "#solutions", linkLabel: "Voir les solutions solaires", testId: "pro-pole-solaire" },
 { title: "Quincaillerie professionnelle", desc: "Produits et consommables destinés aux artisans et aux professionnels. Catégories en cours de constitution.", soon: true, testId: "pro-pole-quincaillerie" },
 { title: "Soudure & équipements", desc: "Matériel, accessoires et consommables de soudure. Catégories en cours de constitution.", soon: true, testId: "pro-pole-soudure" },
 ].map((p) => (
 <StaggerItem key={p.testId} className="bg-white p-10 md:p-12 flex flex-col" >
 <div data-testid={p.testId} className="flex flex-col h-full">
 <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-4">{p.title}</h3>
 <p className="text-nanda-inkSoft text-[15px] leading-relaxed flex-1">{p.desc}</p>
 {p.soon ? (
 <div className="mt-8 inline-flex items-center gap-2 label-overline text-nanda-terracotta" data-testid={`${p.testId}-soon`}>Catalogue à venir</div>
 ) : (
 <a href={p.link} className="mt-8 inline-flex items-center gap-2 label-overline text-nanda-ink hover:text-nanda-terracotta transition-colors" data-testid={`${p.testId}-link`}>
 {p.linkLabel} <ArrowUpRight size={14} strokeWidth={1.5} />
 </a>
 )}
 </div>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* NOS 13 DOMAINES D'EXPERTISE */}
 <Section className="bg-nanda-bgAlt" id="domaines">
 <FadeUp>
 <Overline>Nos domaines d'expertise</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">Treize domaines, un accompagnement de bout en bout.</h2>
 <p className="mt-6 max-w-2xl text-nanda-inkSoft text-[17px] leading-relaxed">
 Chaque projet technique s'appuie sur une ou plusieurs de nos expertises. Notre valeur ajoutée : les faire dialoguer entre elles, pour livrer des installations cohérentes, durables et parfaitement dimensionnées.
 </p>
 </FadeUp>
 <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {[
 { title: "Réfrigération industrielle et commerciale", desc: "Chaîne du froid complète pour l'agroalimentaire, la pharmacie, la logistique et l'hôtellerie." },
 { title: "Chambres froides positives et négatives", desc: "Conception sur mesure, de 0 à +4 °C ou −18 à −25 °C. Modulaires ou maçonnées." },
 { title: "Climatisation professionnelle", desc: "Solutions résidentielles, commerciales, tertiaires et industrielles. Split, cassette, gainable, VRV." },
 { title: "Groupes frigorifiques, évaporateurs, condenseurs", desc: "Sélection, dimensionnement, installation et raccordement des composants centraux du froid." },
 { title: "Panneaux isothermes, portes et accessoires", desc: "Isolation renforcée, portes battantes et coulissantes, accessoires étanches et durables." },
 { title: "Énergie solaire photovoltaïque", desc: "Installations résidentielles, tertiaires, industrielles et agricoles. Toiture ou ombrières." },
 { title: "Stockage, onduleurs et solutions hybrides", desc: "Batteries lithium LFP, onduleurs hybrides, systèmes solaire + diesel + réseau." },
 { title: "Outillage et instruments de mesure", desc: "Équipements frigoristes, groupes de récupération, manomètres, détecteurs de fuites." },
 { title: "Connectique, câblage et protections électriques", desc: "Câbles solaires, disjoncteurs, parafoudres, coffrets, mise à la terre." },
 { title: "Consommables professionnels", desc: "Fluides frigorigènes, huiles, brasures, azote, isolants techniques." },
 { title: "Équipements de protection individuelle (EPI)", desc: "Casques, gants, harnais, chaussures de sécurité, lunettes, EPI thermiques." },
 { title: "Quincaillerie professionnelle", desc: "Produits et consommables pour artisans et professionnels. Catalogue en préparation : références, photos et prix publiés à réception des produits.", soon: true },
 { title: "Soudure & équipements", desc: "Matériel, accessoires et consommables de soudure. Catalogue en préparation : références, photos et prix publiés à réception des produits.", soon: true },
 ].map((d, i) => (
 <div key={i} data-testid={`ep-domain-${i + 1}`} className="bg-white p-8 md:p-9 hover:bg-nanda-bgAlt/50 transition-colors duration-500">
 <div className="label-overline text-nanda-gold text-[10px] mb-4">{String(i + 1).padStart(2, "0")}</div>
 <h3 className="font-serif text-2xl leading-[1.15] mb-3">{d.title}</h3>
 <p className="text-nanda-inkSoft text-[14px] leading-relaxed">{d.desc}</p>
 {d.soon && (
 <div className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-nanda-terracotta" data-testid={`ep-domain-soon-${i + 1}`}>
 Catalogue à venir
 </div>
 )}
 </div>
 ))}
 </div>
 </Section>

 {/* TÉLÉCHARGEMENT CATALOGUE (3 langues) */}
 <Section className="bg-nanda-terracotta text-white" id="catalogue">
 <FadeUp className="max-w-5xl mx-auto">
 <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
 <div className="md:col-span-5">
 <div className="label-overline text-[#F6E7D7] mb-6">Catalogue B2B · Édition 2026</div>
 <h3 className="font-serif text-white text-3xl md:text-5xl lg:text-[52px] leading-[1.05]">
 Notre catalogue<br /><span className="text-[#F6E7D7] italic">en téléchargement libre.</span>
 </h3>
 <p className="mt-6 text-white/75 text-[15px] leading-relaxed max-w-md">
 16 pages présentant nos treize domaines d'intervention, notre méthode de projet et nos zones d'intervention. Format A4, sobre, adapté aux rendez-vous B2B et à l'impression professionnelle.
 </p>
 <div className="mt-6 flex items-center gap-3 text-xs text-white/50 tracking-widest uppercase font-medium">
 <FileText size={14} strokeWidth={1.5} />
 <span>PDF · A4 · 16 pages · ~1,2 Mo</span>
 </div>
 </div>
 <div className="md:col-span-7">
 <div className="grid grid-cols-1 gap-3">
 {[
 { lang: "fr", label: "Français", url: "/pdf/nanda-energia-pro-catalogue-fr.pdf", cta: "Télécharger (FR)" },
 { lang: "en", label: "English", url: "/pdf/nanda-energia-pro-catalogue-en.pdf", cta: "Download (EN)" },
 { lang: "pt", label: "Português", url: "/pdf/nanda-energia-pro-catalogue-pt.pdf", cta: "Descarregar (PT)" },
 ].map((f) => (
 <a
 key={f.lang}
 href={f.url}
 target="_blank"
 rel="noopener noreferrer"
 data-testid={`ep-catalogue-download-${f.lang}`}
 className="group flex items-center justify-between gap-6 px-7 py-6 bg-white/[0.04] border border-white/15 hover:bg-nanda-gold hover:border-nanda-gold transition-all duration-500"
 >
 <div>
 <div className="text-xs tracking-[0.28em] uppercase text-[#F6E7D7] group-hover:text-white font-medium transition-colors duration-500">
 Édition 2026 · 16 pages
 </div>
 <div className="font-serif text-white text-2xl md:text-3xl mt-1 transition-colors duration-500">{f.label}</div>
 </div>
 <div className="flex items-center gap-3 shrink-0 text-white transition-colors duration-500">
 <span className="hidden md:inline text-[11px] tracking-widest uppercase font-medium opacity-85">{f.cta}</span>
 <Download size={20} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-y-0.5" />
 </div>
 </a>
 ))}
 </div>
 <p className="mt-6 text-white/65 text-xs leading-relaxed">
 Onze domaines d'intervention · Processus projet en 6 étapes · Coordonnées commerciales · Adapté impression pro A4.
 </p>
 <p className="mt-3 text-white/60 text-xs tracking-widest uppercase font-medium" data-testid="ep-catalogue-last-update">
 Dernière mise à jour · Février 2026
 </p>
 </div>
 </div>
 </FadeUp>
 </Section>

 {/* PANNEAUX */}
 <Section className="bg-nanda-bgAlt" id="solutions">
 <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
 <FadeUp className="md:col-span-5">
 <div className="aspect-[3/4] bg-white flex items-center justify-center overflow-hidden">
 <img src="/energia/panel-mono.jpg" alt="Panneau photovoltaïque monocristallin NANDA SÁ ENERGIA PRO" className="max-h-full max-w-full object-contain" />
 </div>
 </FadeUp>
 <div className="md:col-span-7">
 <FadeUp>
 <Overline>Nos panneaux photovoltaïques</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">Une gamme monocristalline de haute performance.</h2>
 <p className="text-nanda-inkSoft text-[17px] leading-relaxed max-w-2xl">Cinq puissances, un standard unique. Rendement élevé, résistance aux fortes températures, verre trempé, cadre aluminium, longue durée de vie et garantie constructeur.</p>
 </FadeUp>
 </div>
 </div>

 <Stagger className="mt-16 grid grid-cols-2 md:grid-cols-5 gap-px bg-nanda-line border border-nanda-line">
 {PANELS.map((p) => (
 <StaggerItem key={p.power} className={`p-8 md:p-10 ${p.featured ? "bg-nanda-terracotta text-white relative" : "bg-white"}`}>
 {p.featured && (<div className="absolute top-4 right-4 label-overline text-[#F6E7D7] text-[9px]">Référence</div>)}
 <div className={`font-serif text-4xl md:text-5xl ${p.featured ? "text-white" : "text-nanda-ink"}`}>{p.power}</div>
 <ul className={`mt-5 space-y-2 text-sm ${p.featured ? "text-white/80" : "text-nanda-inkSoft"}`}>
 {p.uses.map((u) => <li key={u}>· {u}</li>)}
 </ul>
 {p.note && <p className="mt-5 text-xs text-white/75 leading-relaxed pt-5 border-t border-white/20">{p.note}</p>}
 </StaggerItem>
 ))}
 </Stagger>

 <FadeUp className="mt-16">
 <div className="label-overline text-nanda-muted mb-6">Pourquoi nos panneaux</div>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
 {["Haute performance", "Technologie monocristalline", "Résistance aux fortes températures", "Verre trempé", "Cadre aluminium", "Longue durée de vie", "Garantie constructeur", "Rendement élevé"].map((t) => (
 <div key={t} className="flex items-start gap-3"><Check size={16} strokeWidth={2} className="text-nanda-gold mt-1 shrink-0" /><span className="text-sm text-nanda-ink">{t}</span></div>
 ))}
 </div>
 </FadeUp>
 </Section>

 {/* MARSTEK VENUS A. Solution compacte tout-en-un */}
 <Section>
 <FadeUp className="max-w-3xl mb-14">
 <Overline>Nos solutions de stockage · Solution compacte</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">MARSTEK Venus A.</h2>
 <p className="mt-6 text-xl md:text-2xl text-nanda-inkSoft font-serif italic">Système de stockage d'énergie tout-en-un.</p>
 </FadeUp>

 <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
 <FadeUp className="md:col-span-7">
 <div className="bg-white border border-nanda-line p-6 md:p-10 flex items-center justify-center">
 <img src="/energia/marstek-venus-a.jpg" alt="MARSTEK Venus A, système de stockage d'énergie tout-en-un" className="max-w-full h-auto" loading="lazy" />
 </div>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-5 text-nanda-inkSoft text-[17px] leading-relaxed space-y-4">
 <p>Le MARSTEK Venus A associe un <strong className="text-nanda-ink font-medium">onduleur hybride</strong> et une <strong className="text-nanda-ink font-medium">batterie lithium LiFePO₄</strong> dans un seul équipement compact.</p>
 <p>Il stocke l'énergie produite par vos panneaux photovoltaïques et vous permet d'améliorer sensiblement votre autonomie énergétique.</p>
 <Link to="/devis" className="inline-flex items-center gap-3 mt-6 bg-nanda-terracotta text-white px-8 py-4 label-overline hover:bg-nanda-gold transition-colors duration-500" data-testid="marstek-cta-devis">Demander un devis <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
 </FadeUp>
 </div>

 {/* Points forts */}
 <FadeUp className="mt-20 mb-6"><Overline>Les points forts</Overline></FadeUp>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6">
 {["Onduleur hybride intégré", "Batterie Lithium LiFePO₄", "Capacité 2,12 kWh", "Design compact", "Installation simplifiée", "Fonctionnement silencieux", "Sécurité élevée", "Longue durée de vie"].map((t) => (
 <div key={t} className="flex items-start gap-3 py-3 border-t border-nanda-line"><Check size={16} strokeWidth={2} className="text-nanda-gold mt-1 shrink-0" /><span className="text-sm text-nanda-ink leading-snug">{t}</span></div>
 ))}
 </div>

 {/* Applications */}
 <FadeUp className="mt-20 mb-8"><Overline>Applications</Overline></FadeUp>
 <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-nanda-line border border-nanda-line">
 {["Maison", "Appartement", "Boutique", "Bureau", "Petit commerce", "Cabinet professionnel"].map((a) => (
 <StaggerItem key={a} className="bg-white p-6 md:p-8 text-center">
 <div className="font-serif text-lg md:text-xl text-nanda-ink">{a}</div>
 </StaggerItem>
 ))}
 </Stagger>

 {/* Pourquoi + lifestyle */}
 <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
 <FadeUp className="md:col-span-6">
 <Overline>Pourquoi choisir MARSTEK Venus A</Overline>
 <h3 className="font-serif text-3xl md:text-4xl leading-[1.1] mt-4 mb-6">Compact. Simple. Intelligent.</h3>
 <ul className="space-y-3">
 {[
 "Réduire la dépendance au réseau électrique",
 "Optimiser l'autoconsommation solaire",
 "Assurer une alimentation de secours (selon configuration)",
 "Préparer l'évolution future de l'installation",
 ].map((b) => (
 <li key={b} className="flex items-start gap-3"><Check size={18} strokeWidth={2} className="text-nanda-gold mt-1 shrink-0" /><span className="text-nanda-inkSoft">{b}</span></li>
 ))}
 </ul>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-6">
 <img src="/energia/marstek-lifestyle.jpg" alt="MARSTEK en usage résidentiel, autonomie énergétique domestique" className="w-full h-auto border border-nanda-line" loading="lazy" />
 </FadeUp>
 </div>
 </Section>

 {/* STOCKAGE Q.SAVE-G3. SECTION PREMIUM ÉTENDUE */}
 <Section className="bg-nanda-bgAlt">
 {/* HERO STOCKAGE */}
 <FadeUp className="max-w-3xl mb-16">
 <Overline>Systèmes de stockage d'énergie intelligents</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Stockez votre énergie aujourd'hui,<br />profitez-en à tout moment.</h2>
 </FadeUp>

 {/* Photo HD gamme complète */}
 <FadeUp className="mb-20">
 <div className="bg-white border border-nanda-line p-6 md:p-10 lg:p-14">
 <img src="/energia/qcells-qsave-gamme.jpg" alt="Gamme complète Qcells Q.SAVE-G3 : 3 kWh, 6 kWh, 9 kWh, 12 kWh avec dimensions" className="w-full h-auto" loading="lazy" />
 </div>
 </FadeUp>

 {/* PRÉSENTATION */}
 <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
 <FadeUp className="md:col-span-5">
 <Overline>Présentation</Overline>
 <h3 className="font-serif text-3xl md:text-4xl leading-[1.1]">Une énergie disponible jour et nuit.</h3>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] leading-relaxed space-y-4">
 <p>Les systèmes Qcells Q.SAVE-G3 conservent l'électricité produite par vos panneaux photovoltaïques pour vous permettre de l'utiliser lorsque vous en avez besoin, de jour comme de nuit, même en cas de coupure réseau.</p>
 <p>Grâce à leur conception modulaire, ces solutions s'adaptent à l'évolution de vos besoins énergétiques tout en offrant une maintenance simplifiée et une excellente durée de vie.</p>
 </FadeUp>
 </div>

 {/* 4 CAPACITÉS, cartes enrichies */}
 <FadeUp className="mb-6"><Overline>Les capacités disponibles</Overline></FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {[
 { c: "3 kWh", h: "ca. 136,5 cm · 50 cm", target: "Solution idéale pour", uses: ["Appartement", "Petite maison", "Boutique", "Bureau"], note: "Convient aux besoins essentiels, avec possibilité d'évoluer." },
 { c: "6 kWh", h: "ca. 163,5 cm · 50 cm", target: "Recommandé pour", uses: ["Maison familiale", "Commerce", "Restaurant", "Petit atelier"], note: "Plus d'autonomie pour un confort quotidien." },
 { c: "9 kWh", h: "ca. 190,5 cm · 50 cm", target: "Conçu pour", uses: ["Villa", "Hôtel", "Exploitation agricole", "PME"], note: "Une capacité adaptée aux besoins énergétiques plus importants." },
 { c: "12 kWh", h: "ca. 190,5 cm · 50 cm", target: "Idéal pour", uses: ["Entreprises", "Hôtels", "Écoles", "Centres de santé", "Bâtiments professionnels"], note: "Une solution fiable pour les installations à forte autonomie." },
 ].map((cap) => (
 <StaggerItem key={cap.c} className="bg-white p-8 md:p-10 flex flex-col">
 <Battery size={24} strokeWidth={1.2} className="text-nanda-gold mb-5" />
 <div className="font-serif text-3xl md:text-4xl text-nanda-ink">Q.SAVE-G3 {cap.c}</div>
 <div className="mt-2 text-[11px] tracking-wide text-nanda-muted">{cap.h}</div>
 <div className="mt-5 label-overline text-nanda-muted text-[10px]">{cap.target}</div>
 <ul className="mt-3 space-y-1.5 text-sm text-nanda-inkSoft flex-1">
 {cap.uses.map((u) => <li key={u}>· {u}</li>)}
 </ul>
 <p className="mt-6 text-xs text-nanda-inkSoft leading-relaxed italic pt-5 border-t border-nanda-line">{cap.note}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* MODULARITÉ */}
 <Section className="bg-nanda-bgAlt">
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14 items-center">
 <FadeUp className="md:col-span-6">
 <Overline>Une solution modulaire</Overline>
 <h3 className="font-serif text-4xl md:text-5xl leading-[1.05] mb-8">Votre installation évolue avec vous.</h3>
 <div className="text-nanda-inkSoft text-[17px] leading-relaxed space-y-4">
 <p>Les systèmes Qcells Q.SAVE-G3 sont entièrement modulaires. Vous pouvez commencer avec une capacité de 3 kWh, puis augmenter progressivement votre stockage en fonction de l'évolution de vos besoins.</p>
 <p>Pas besoin de remplacer l'ensemble du système : il suffit d'ajouter un module compatible. Vous maîtrisez votre investissement, tout en préparant l'avenir.</p>
 </div>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-6">
 <div className="bg-white border border-nanda-line p-10 md:p-12">
 <div className="label-overline text-nanda-muted mb-8">Chemins d'évolution</div>
 <div className="space-y-6">
 {[["3 kWh", "6 kWh"], ["6 kWh", "9 kWh"], ["9 kWh", "12 kWh"]].map(([from, to]) => (
 <div key={from} className="flex items-center gap-6 py-4 border-b border-nanda-line last:border-b-0">
 <div className="font-serif text-2xl md:text-3xl text-nanda-ink w-24">{from}</div>
 <div className="flex-1 h-px bg-nanda-gold relative">
 <ArrowUpRight size={16} strokeWidth={1.5} className="absolute -right-3 -top-2 text-nanda-gold rotate-45" />
 </div>
 <div className="font-serif text-2xl md:text-3xl text-nanda-gold w-24 text-right">{to}</div>
 </div>
 ))}
 </div>
 <p className="mt-8 text-xs text-nanda-muted italic">Ajouter, ne pas remplacer.</p>
 </div>
 </FadeUp>
 </div>
 </Section>

 {/* MAINTENANCE ÉCONOMIQUE */}
 <Section>
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>Un entretien simple et économique</Overline>
 <h3 className="font-serif text-4xl md:text-5xl leading-[1.05]">Remplacez un module, pas tout le système.</h3>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14 items-start">
 <FadeUp className="md:col-span-6 text-nanda-inkSoft text-[17px] leading-relaxed space-y-4">
 <p>L'architecture modulaire des Q.SAVE-G3 change la donne : si un module devient défaillant après plusieurs années, il est possible de le remplacer seul, sans toucher au reste de l'installation.</p>
 <p>Résultat : une maintenance plus économique, plus rapide, et une installation qui dure.</p>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-6">
 <ul className="space-y-4">
 {["Réduction des coûts de maintenance", "Remplacement rapide", "Immobilisation limitée", "Investissement préservé", "Durée de vie prolongée de l'installation"].map((b) => (
 <li key={b} className="flex items-start gap-4 py-3 border-b border-nanda-line last:border-b-0"><Check size={18} strokeWidth={2} className="text-nanda-gold mt-0.5 shrink-0" /><span className="text-nanda-ink">{b}</span></li>
 ))}
 </ul>
 </FadeUp>
 </div>
 </Section>

 {/* POURQUOI Q.SAVE-G3 */}
 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>Pourquoi choisir Q.SAVE-G3</Overline>
 <h3 className="font-serif text-4xl md:text-5xl leading-[1.05]">Dix raisons, une seule référence.</h3>
 </FadeUp>
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-6">
 {["Technologie Lithium LiFePO₄", "Conception modulaire", "Évolutif selon vos besoins", "Haute sécurité", "Longue durée de vie", "Maintenance simplifiée", "Design élégant", "Installation au sol", "Compatible résidentiel & pro", "Adapté à l'Afrique de l'Ouest"].map((t) => (
 <div key={t} className="flex items-start gap-3 py-3 border-t border-nanda-line"><Check size={16} strokeWidth={2} className="text-nanda-gold mt-1 shrink-0" /><span className="text-sm text-nanda-ink leading-snug">{t}</span></div>
 ))}
 </div>
 </Section>

 {/* COMPARAISON DEUX GAMMES */}
 <Section>
 <FadeUp className="max-w-3xl mb-14">
 <Overline>Nos deux gammes de stockage · comparatif</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Deux réponses, un même standard.</h2>
 <p className="mt-6 text-nanda-inkSoft text-[17px] leading-relaxed">Du petit système résidentiel jusqu'aux installations tertiaires, NANDA SÁ ENERGIA PRO propose deux gammes complémentaires. Vous choisissez selon vos besoins actuels, sachant que l'évolution reste possible.</p>
 </FadeUp>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-nanda-line border border-nanda-line">
 {/* MARSTEK */}
 <div className="bg-white p-10 md:p-12">
 <div className="label-overline text-nanda-gold mb-4">Solution compacte</div>
 <h3 className="font-serif text-3xl md:text-4xl text-nanda-ink">MARSTEK Venus A</h3>
 <p className="mt-4 text-sm text-nanda-inkSoft italic">Onduleur hybride + batterie 2,12 kWh en un seul boîtier.</p>
 <div className="mt-8 space-y-4 text-sm">
 <div className="flex justify-between border-b border-nanda-line pb-3"><span className="text-nanda-muted">Type</span><span className="text-nanda-ink font-medium">Tout-en-un</span></div>
 <div className="flex justify-between border-b border-nanda-line pb-3"><span className="text-nanda-muted">Capacité</span><span className="text-nanda-ink font-medium">2,12 kWh</span></div>
 <div className="flex justify-between border-b border-nanda-line pb-3"><span className="text-nanda-muted">Chimie</span><span className="text-nanda-ink font-medium">Lithium LiFePO₄</span></div>
 <div className="flex justify-between border-b border-nanda-line pb-3"><span className="text-nanda-muted">Idéal pour</span><span className="text-nanda-ink font-medium">Résidentiel · petit pro</span></div>
 <div className="flex justify-between pb-1"><span className="text-nanda-muted">Encombrement</span><span className="text-nanda-ink font-medium">Compact</span></div>
 </div>
 </div>
 {/* QCELLS */}
 <div className="bg-white p-10 md:p-12">
 <div className="label-overline text-nanda-gold mb-4">Solution modulaire</div>
 <h3 className="font-serif text-3xl md:text-4xl text-nanda-ink">Qcells Q.SAVE-G3</h3>
 <p className="mt-4 text-sm text-nanda-inkSoft italic">Stockage modulaire de 3 à 12 kWh, évolutif dans le temps.</p>
 <div className="mt-8 space-y-4 text-sm">
 <div className="flex justify-between border-b border-nanda-line pb-3"><span className="text-nanda-muted">Type</span><span className="text-nanda-ink font-medium">Modulaire</span></div>
 <div className="flex justify-between border-b border-nanda-line pb-3"><span className="text-nanda-muted">Capacité</span><span className="text-nanda-ink font-medium">3 · 6 · 9 · 12 kWh</span></div>
 <div className="flex justify-between border-b border-nanda-line pb-3"><span className="text-nanda-muted">Chimie</span><span className="text-nanda-ink font-medium">Lithium LiFePO₄</span></div>
 <div className="flex justify-between border-b border-nanda-line pb-3"><span className="text-nanda-muted">Idéal pour</span><span className="text-nanda-ink font-medium">Villas · hôtels · PME · collectivités</span></div>
 <div className="flex justify-between pb-1"><span className="text-nanda-muted">Évolutif</span><span className="text-nanda-ink font-medium">Oui, par ajout de modules</span></div>
 </div>
 </div>
 </div>

 <p className="mt-10 text-sm text-nanda-muted italic text-center max-w-2xl mx-auto">Nos équipes vous conseillent sur la solution la mieux adaptée à votre projet, votre consommation et votre budget.</p>
 </Section>

 {/* ENCADRÉ PREMIUM. Positionnement des deux gammes */}
 <Section className="bg-nanda-terracotta text-white">
 <FadeUp className="max-w-4xl mx-auto text-center">
 <Overline><span className="text-[#F6E7D7]">L'engagement NANDA SÁ ENERGIA PRO</span></Overline>
 <h3 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.08] mt-4">Une solution compacte.<br /><span className="text-[#F6E7D7] italic">Une énergie intelligente.</span></h3>
 <p className="mt-10 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">Que vous cherchiez la simplicité d'une solution tout-en-un ou la flexibilité d'un stockage modulaire, nous vous accompagnons du dimensionnement à la maintenance, avec la même exigence.</p>
 <div className="mt-12"><Link to="/devis" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-10 py-5 label-overline hover:bg-nanda-sand transition-colors duration-500" data-testid="stockage-cta-final">Étudier mon projet stockage <ArrowUpRight size={16} strokeWidth={1.5} /></Link></div>
 </FadeUp>
 </Section>

 {/* KITS COMPLETS */}
 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-16 max-w-3xl">
 <Overline>Nos kits complets</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Cinq configurations pensées pour vos usages.</h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-nanda-line border border-nanda-line">
 {KITS.map((k) => (
 <StaggerItem key={k.name} className="bg-white p-8 md:p-10 flex flex-col">
 <Zap size={28} strokeWidth={1.2} className="text-nanda-gold mb-6" />
 <div className="font-serif text-3xl text-nanda-ink">{k.name}</div>
 <div className="mt-3 text-xs text-nanda-muted leading-relaxed">{k.target}</div>
 <ul className="mt-6 space-y-2 text-sm text-nanda-inkSoft flex-1">
 {k.items.map((i) => <li key={i}>· {i}</li>)}
 </ul>
 <Link to="/devis" className="mt-8 inline-flex items-center gap-2 label-overline text-nanda-ink hover:text-nanda-gold transition-colors">Demander un devis <ArrowUpRight size={14} strokeWidth={1.5} /></Link>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* GRAND BANDEAU RÉALISATIONS */}
 <section className="relative h-[70vh] min-h-[500px] w-full overflow-hidden bg-nanda-ink">
 <img src="/energia-pro-installation.jpg" alt="Réalisations NANDA SÁ ENERGIA PRO" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
 <div className="absolute inset-0 bg-nanda-ink/55" />
 <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-center">
 <FadeUp>
 <Overline><span className="text-white/75">Nos réalisations</span></Overline>
 <h2 className="font-serif text-white text-4xl md:text-6xl lg:text-7xl leading-[1.05] max-w-3xl">Des installations menées avec rigueur, par nos équipes.</h2>
 <div className="mt-10"><Link to="/devis" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-8 py-4 label-overline hover:bg-nanda-sand transition-colors duration-500">Obtenir mon devis <ArrowUpRight size={16} strokeWidth={1.5} /></Link></div>
 </FadeUp>
 </div>
 </section>

 {/* POURQUOI NOUS */}
 <Section>
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>Pourquoi choisir NANDA SÁ ENERGIA PRO</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Un partenaire, du premier échange à la maintenance.</h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {[
 { i: Sun, t: "Étude personnalisée", d: "Analyse énergétique, dimensionnement, choix des équipements." },
 { i: Wrench, t: "Installation professionnelle", d: "Équipes techniques locales formées, sécurité chantier conforme." },
 { i: Award, t: "Matériel sélectionné", d: "Fabricants reconnus à l'international, traçabilité garantie." },
 { i: ShieldCheck, t: "Garantie constructeur", d: "Les certificats et garanties d'origine sont transmis au client." },
 { i: Battery, t: "Maintenance & SAV", d: "Suivi long terme, pièces d'origine, service de proximité." },
 { i: Zap, t: "Solutions sur mesure", d: "Résidentiel, tertiaire, industriel, agricole ou institutionnel." },
 ].map((b) => (
 <StaggerItem key={b.t} className="bg-white p-10">
 <b.i size={28} strokeWidth={1.2} className="text-nanda-gold mb-6" />
 <div className="font-serif text-2xl text-nanda-ink">{b.t}</div>
 <p className="mt-4 text-sm text-nanda-inkSoft leading-relaxed">{b.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* MÉTHODE */}
 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>Notre méthode</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Huit étapes, une seule exigence.</h2>
 </FadeUp>
 <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {METHOD.map((step, i) => (
 <StaggerItem key={step} className="bg-white p-8 md:p-10">
 <div className="font-serif text-nanda-gold text-3xl mb-4">0{i + 1}</div>
 <div className="font-serif text-xl text-nanda-ink">{step}</div>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* FAQ */}
 <Section>
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>Foire aux questions</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Vos questions, nos réponses.</h2>
 </FadeUp>
 <div className="max-w-4xl divide-y divide-nanda-line border-y border-nanda-line">
 {FAQ.map((f, i) => (
 <div key={i}>
 <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-6 py-6 text-left group" data-testid={`energia-faq-${i}`}>
 <span className="font-serif text-lg md:text-xl text-nanda-ink group-hover:text-nanda-gold transition-colors">{f.q}</span>
 <ChevronDown size={20} strokeWidth={1.5} className={`shrink-0 text-nanda-muted transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
 </button>
 {openFaq === i && <p className="pb-6 pr-14 text-nanda-inkSoft leading-relaxed">{f.a}</p>}
 </div>
 ))}
 </div>
 </Section>

 {/* CTA FINAL */}
 <Section className="bg-nanda-terracotta text-white">
 <div className="text-center max-w-3xl mx-auto">
 <FadeUp>
 <Overline><span className="text-[#F6E7D7]">Recevoir votre devis</span></Overline>
 <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-8">Parlons de votre projet.</h2>
 <p className="text-white/75 text-lg leading-relaxed mb-10">Nom, activité, localisation, type de bâtiment, consommation estimée. Nous revenons vers vous rapidement, avec une proposition sur mesure.</p>
 <Link to="/devis" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-10 py-5 label-overline hover:bg-nanda-sand transition-colors duration-500" data-testid="energia-cta-final">Recevoir mon devis personnalisé <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
 </FadeUp>
 </div>
 </Section>
 </div>
 );
}
